// mattermost-root-post-sender-with-marker.ts
//
// X1 (close-out 2026-09-30): the production sender for the mission root post.
// Every root post carries `props.avatar_correlation_id`, and
// `recoverByCorrelationId` looks for it among the channel's posts since the
// mission was created (only the bot's own ROOT posts count). That is what lets
// mattermost-root-post-manager.ts reconcile an `uncertain` send (5xx/timeout
// after the server may have committed it) instead of posting a second root.
// "Absent" (`undefined`) is returned ONLY when the lookup provably reached
// back past the mission's creation; anything short of that throws — a failed
// listing, the page cap, an unknown bot id — and the manager keeps the op
// `uncertain` (review P4 M2/L1: `since` is capped server-side and compared
// with the server clock, so it cannot prove absence on a busy shared channel).

import type { MattermostRestClient } from "./mattermost-rest-client.js";
import type { RootPostSender } from "./mattermost-root-post-manager.js";

export const ROOT_POST_CORRELATION_PROP = "avatar_correlation_id";

const PAGE_SIZE = 200;
/** Pages scanned before giving up as "cannot tell" (4000 posts). */
const MAX_PAGES = 20;
/** Tolerated local/server clock skew when deciding a page reached back far enough. */
const CLOCK_SKEW_MS = 5 * 60 * 1000;

export function buildRootPostSenderWithMarker(input: {
  client: Pick<MattermostRestClient, "createPost" | "getPostsForChannel">;
  channelId: string;
  correlationId: string;
  /** Mission creation time (ms) — posts before it cannot be this mission's root. */
  sinceMs: number;
  /** The bot's user id; `""` (getMe failed non-fatally) ⇒ lookup cannot tell. */
  botUserId: string;
}): RootPostSender {
  return {
    createPost: (channel, message, rootId) =>
      input.client.createPost(channel, message, rootId, {
        [ROOT_POST_CORRELATION_PROP]: input.correlationId,
      }),
    recoverByCorrelationId: async (correlationId) => {
      if (input.botUserId === "") throw new Error("bot user id unknown — cannot attribute posts");
      const floor = input.sinceMs - CLOCK_SKEW_MS;
      for (let page = 0; page < MAX_PAGES; page++) {
        const list = await input.client.getPostsForChannel(input.channelId, page, PAGE_SIZE);
        const posts = list.order.map((id) => list.posts[id]).filter((p) => p !== undefined);
        const match = posts.find(
          (post) =>
            post.props?.[ROOT_POST_CORRELATION_PROP] === correlationId &&
            post.root_id === "" &&
            post.user_id === input.botUserId,
        );
        if (match !== undefined) return { id: match.id, message: match.message };
        const reachedBack = posts.some((post) => post.create_at < floor);
        if (reachedBack || list.order.length < PAGE_SIZE) return undefined;
      }
      throw new Error(`root post lookup hit the ${MAX_PAGES}-page cap — cannot tell`);
    },
  };
}
